import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

const infoIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <circle cx="12" cy="12" r="10" />
  <path d="M12 16v-4" />
  <path d="M12 8h.01" />
</svg>`;

class SpellOptionCard extends BaseComponent {
  static properties = {
    name: { type: String },
    slug: { type: String },
    school: { type: String },
    concentration: { type: Boolean },
    description: { type: String },
    selected: { type: Boolean },
    disabled: { type: Boolean },
    recommended: { type: Boolean },
    known: { type: Boolean },
    inputType: { type: String, attribute: 'input-type' },
    radioGroup: { type: String, attribute: 'radio-group' },
  };

  static styles = css`
    :host {
      display: block;
    }
    .card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
      height: 100%;
    }
    .card:hover:not(.disabled) {
      border-color: #0d6efd;
    }
    .card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }
    .card.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .info-icon {
      color: #6c757d;
      cursor: pointer;
    }
  `;

  constructor() {
    super();
    this.name = '';
    this.slug = '';
    this.school = '';
    this.concentration = false;
    this.description = '';
    this.selected = false;
    this.disabled = false;
    this.recommended = false;
    this.known = false;
    this.inputType = 'checkbox';
    this.radioGroup = '';
  }

  _buildTooltip() {
    let tip = this.school ? this.school.charAt(0).toUpperCase() + this.school.slice(1) : '';
    if (this.concentration) tip += ' (Concentration)';
    if (this.description) tip += (tip ? ' — ' : '') + this.description;
    return tip;
  }

  _onClick() {
    if (this.disabled) return;
    this.dispatchEvent(
      new CustomEvent('card-selected', {
        detail: { slug: this.slug },
        bubbles: true,
        composed: true,
      }),
    );
  }

  updated() {
    this._initTooltips();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disposeTooltips();
  }

  _initTooltips() {
    this._disposeTooltips();
    const Tooltip = /** @type {{bootstrap?: {Tooltip: {new(el: Element, opts?: object): {dispose(): void}}}}} */ (
      window
    ).bootstrap?.Tooltip;
    if (!Tooltip) return;
    if (!document.querySelector('#spell-tooltip-style')) {
      const style = document.createElement('style');
      style.id = 'spell-tooltip-style';
      style.textContent = '.spell-tooltip .tooltip-inner { max-width: 350px; text-align: left; }';
      document.head.appendChild(style);
    }
    /** @type {{dispose: Function}[] | undefined} */
    this._tooltipInstances = [];
    const icons = this.renderRoot?.querySelectorAll('[data-bs-toggle="tooltip"]') || [];
    icons.forEach((el) => {
      this._tooltipInstances?.push(new Tooltip(el, { container: 'body', customClass: 'spell-tooltip' }));
    });
  }

  _disposeTooltips() {
    if (this._tooltipInstances) {
      this._tooltipInstances.forEach((t) => {
        t.dispose();
      });
      this._tooltipInstances = undefined;
    }
  }

  render() {
    const tooltip = this._buildTooltip();
    return html`
      <div class="card ${this.selected ? 'selected' : ''} ${this.disabled ? 'disabled' : ''}" @click="${this._onClick}">
        <div class="card-body py-2 px-3">
          <div class="d-flex align-items-center">
            <input
              type="${this.inputType}"
              class="form-check-input me-2"
              .checked="${this.selected}"
              ?disabled="${this.disabled}"
              name="${this.radioGroup}"
              @click="${(/** @type {Event} */ e) => {
                e.stopPropagation();
                this._onClick();
              }}"
            />
            <span class="fw-medium">${this.name}</span>${
              this.known
                ? html`<span class="badge bg-secondary bg-opacity-25 text-dark ms-2">Known</span>`
                : this.recommended
                  ? html`<span class="badge bg-primary bg-opacity-25 text-primary ms-2">Recommended</span>`
                  : ''
            }
            ${
              tooltip
                ? html`
                  <span class="ms-auto info-icon" data-bs-toggle="tooltip" data-bs-title="${tooltip}">
                    ${infoIcon}
                  </span>
                `
                : ''
            }
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('spell-option-card', SpellOptionCard);
